import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, ExternalLink, Check, X, GraduationCap, Bell, Wallet } from 'lucide-react';
import {
  getApplication, verifyDocuments, requestDocument, remind, confirmPayment, approvePayment, rejectPayment, matchPayment,
  scheduleExam, examScore, decide, admit, getExams, naira, errText, type ClassGroup,
} from '@/api/admissions';
import { Drawer, Card, StatusPill, Pill, Btn, Modal, Field, input, Notice, fmtDate } from './ui';

export function ApplicationDrawer({ id, groups, onClose }: { id: number; groups: ClassGroup[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [notice, setNotice] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [modal, setModal] = useState<null | 'admit' | 'reject' | 'office' | 'reject_receipt' | 'request_doc' | 'score'>(null);
  const [target, setTarget] = useState<any>(null);

  const aQ = useQuery({ queryKey: ['adm-app', id], queryFn: () => getApplication(id) });
  const a: any = aQ.data;
  const examsQ = useQuery({ queryKey: ['adm-exams', a?.admission_cycle_id], queryFn: () => getExams(a.admission_cycle_id), enabled: !!a });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['adm-app', id] });
    qc.invalidateQueries({ queryKey: ['adm-apps'] });
    qc.invalidateQueries({ queryKey: ['adm-summary'] });
    qc.invalidateQueries({ queryKey: ['adm-payments'] });
  };
  const run = useMutation({
    mutationFn: (fn: () => Promise<any>) => fn(),
    onSuccess: (r: any, _v) => { refresh(); setModal(null); setNotice({ tone: 'ok', text: r?.message || 'Saved.' }); },
    onError: (e) => setNotice({ tone: 'err', text: errText(e) }),
  });
  const act = (fn: () => Promise<any>, ok: string) => run.mutate(async () => { await fn(); return { message: ok }; });

  if (!a) {
    return <Drawer title="Application" onClose={onClose}><p className="text-sm text-slate-500">{aQ.isError ? errText(aQ.error) : 'Loading…'}</p></Drawer>;
  }

  const extra = a.extra ?? {};
  const group = groups.find((g) => g.class_ids.includes(a.class_applied_id));
  const arms = a.class_arms?.length ? a.class_arms : group?.arms ?? [];
  const exam = a.exam_results?.[a.exam_results.length - 1];
  const pendingProof = (a.payments ?? []).find((p: any) => p.status === 'pending' && p.review === 'pending');
  const closed = ['enrolled', 'rejected', 'declined'].includes(a.status);
  const cycleExams = (examsQ.data ?? []).filter((e) => !group || e.class_names.length === 0 || e.class_names.some((n) => n.replace(/\W/g, '').toUpperCase() === group.key));

  return (
    <Drawer title={a.name} sub={<span className="flex flex-wrap items-center gap-2">{a.reference} · {a.class_applied?.name ?? 'No class'} <StatusPill status={a.status} /></span>} onClose={onClose}>
      {notice && <Notice tone={notice.tone} onClose={() => setNotice(null)}>{notice.text}</Notice>}

      {a.admission && (
        <div className="rounded-xl bg-green-50 border border-green-200 p-4">
          <p className="text-sm font-bold text-green-900 flex items-center gap-2"><GraduationCap className="w-4 h-4" /> Admitted into {a.admission.class?.name}</p>
          <p className="text-sm text-green-800 mt-1">Reg no <b>{a.admission.admission_no}</b> · sent to the applicant dashboard{a.guardian_phone ? ' and by SMS' : ''}.</p>
        </div>
      )}

      {/* Next actions */}
      {!closed && (
        <div className="grid grid-cols-2 gap-2">
          <Btn onClick={() => { setTarget(null); setModal('admit'); }} disabled={!a.fee.paid}><GraduationCap className="w-4 h-4" /> Admit</Btn>
          <Btn kind="ghost" onClick={() => setModal('reject')}>Waitlist / reject</Btn>
          {!a.fee.paid && <Btn kind="soft" onClick={() => setModal('office')}><Wallet className="w-4 h-4" /> Paid at office</Btn>}
          {!a.fee.paid && <Btn kind="soft" onClick={() => act(() => remind(a.id), 'Reminder sent.')}><Bell className="w-4 h-4" /> Remind to pay</Btn>}
        </div>
      )}
      {!a.fee.paid && !closed && <p className="text-xs text-amber-700 -mt-2">Admit unlocks once the application fee ({naira(a.fee.amount)}) is paid.</p>}

      {/* Receipt to check */}
      {pendingProof && (
        <Card title="Receipt to verify" sub={`Uploaded ${fmtDate(pendingProof.proof?.submitted_at, true)}`}>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <dt className="text-slate-500">Amount due</dt><dd className="font-semibold text-right">{naira(pendingProof.amount)}</dd>
            <dt className="text-slate-500">Amount paid</dt><dd className="font-semibold text-right">{pendingProof.proof?.amount ? naira(pendingProof.proof.amount) : '—'}</dd>
            <dt className="text-slate-500">Transaction ref</dt><dd className="font-mono text-right break-all">{pendingProof.proof?.transaction_reference}</dd>
            <dt className="text-slate-500">Paid on</dt><dd className="text-right">{pendingProof.proof?.paid_on ?? '—'}</dd>
          </dl>
          {pendingProof.proof?.url && <a href={pendingProof.proof.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-green-700"><ExternalLink className="w-4 h-4" />Open receipt</a>}
          <div className="grid grid-cols-3 gap-2 mt-4">
            <Btn onClick={() => act(() => approvePayment(pendingProof.id), 'Payment approved.')}><Check className="w-4 h-4" />Approve</Btn>
            <Btn kind="soft" onClick={() => act(() => matchPayment(pendingProof.id), 'Matched with your XpressWallet and approved.')}>Match wallet</Btn>
            <Btn kind="ghost" onClick={() => { setTarget(pendingProof); setModal('reject_receipt'); }}><X className="w-4 h-4" />Reject</Btn>
          </div>
        </Card>
      )}

      {/* Exam */}
      <Card title="Entrance exam" action={!closed && a.fee.paid && cycleExams.length > 0 && (!exam || exam.status === 'scheduled') ? (
        <select className="border border-slate-300 rounded-lg text-xs px-2 py-1.5" value="" onChange={(e) => e.target.value && act(() => scheduleExam(a.id, Number(e.target.value)), 'Exam booked.')}>
          <option value="">{exam ? 'Move to another exam…' : 'Book an exam…'}</option>
          {cycleExams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      ) : null}>
        {!exam ? <p className="text-sm text-slate-500">{a.fee.paid ? 'Not booked yet. Applicants are booked automatically when an exam exists for their class.' : 'Booked automatically after payment.'}</p> : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div><p className="text-sm font-semibold text-slate-900">{exam.exam_name}</p><p className="text-xs text-slate-500">{exam.mode === 'written' ? 'Written' : 'CBT'} · {exam.scheduled_at ? fmtDate(exam.scheduled_at, true) : 'Open now'}</p></div>
              {exam.percentage != null
                ? <div className="text-right"><p className={`text-2xl font-extrabold ${exam.percentage >= exam.pass_mark ? 'text-green-700' : 'text-red-600'}`}>{exam.percentage}%</p><p className="text-[11px] text-slate-500">Pass mark {exam.pass_mark}%</p></div>
                : <Pill tone="violet">{exam.status === 'scheduled' ? 'Booked' : exam.status}</Pill>}
            </div>
            {exam.subject_scores?.length > 0 && (
              <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
                {exam.subject_scores.map((s: any) => (
                  <li key={s.subject_id} className="flex items-center justify-between px-3 py-2 text-sm"><span>{s.name}</span><span className="font-semibold">{s.score}/{s.max} <span className="text-slate-400 font-normal">({s.percentage}%)</span></span></li>
                ))}
              </ul>
            )}
            {!closed && <button onClick={() => setModal('score')} className="text-xs font-semibold text-slate-600 underline">{exam.mode === 'written' ? 'Enter written score' : 'Override score / mark absent'}</button>}
          </div>
        )}
      </Card>

      {/* Documents */}
      <Card title="Documents" action={!closed && <button onClick={() => setModal('request_doc')} className="text-xs font-semibold text-green-700">Request a document</button>}>
        <ul className="space-y-2">
          {(a.required_documents ?? []).map((rd: any) => {
            const doc = (a.documents ?? []).find((d: any) => d.type === rd.type);
            return (
              <li key={rd.type} className="flex items-center gap-3 text-sm">
                <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="flex-1 min-w-0"><span className="font-medium text-slate-900">{rd.label}</span>{!rd.required && <span className="text-xs text-slate-400"> · optional</span>}</span>
                {doc ? (
                  <span className="flex items-center gap-2">
                    <a href={doc.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-green-700">View</a>
                    {doc.status === 'pending'
                      ? <><button onClick={() => act(() => verifyDocuments(a.id, { documents: [{ id: doc.id, status: 'verified' }] }), 'Document verified.')} className="text-xs font-semibold text-slate-700">Verify</button>
                        <button onClick={() => act(() => verifyDocuments(a.id, { documents: [{ id: doc.id, status: 'rejected' }] }), 'Document rejected.')} className="text-xs font-semibold text-red-600">Reject</button></>
                      : <Pill tone={doc.status === 'verified' ? 'green' : 'red'}>{doc.status}</Pill>}
                  </span>
                ) : <Pill tone={rd.required ? 'amber' : 'slate'}>Missing</Pill>}
              </li>
            );
          })}
          {(a.documents ?? []).filter((d: any) => !(a.required_documents ?? []).some((r: any) => r.type === d.type)).map((d: any) => (
            <li key={d.id} className="flex items-center gap-3 text-sm"><FileText className="w-4 h-4 text-slate-400" /><span className="flex-1 capitalize">{d.type.replace(/_/g, ' ')}</span><a href={d.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-green-700">View</a></li>
          ))}
        </ul>
      </Card>

      {/* Details */}
      <Card title="Applicant details">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {[
            ['Full name', [a.first_name, extra.middle_name, a.last_name].filter(Boolean).join(' ')], ['Gender', a.gender], ['Date of birth', a.date_of_birth],
            ['Class applied', a.class_applied?.name], ['Previous school', a.previous_school], ['Religion', extra.religion], ['State of origin', extra.state_of_origin],
            ['Nationality', extra.nationality], ['Blood group', extra.blood_group], ['Medical notes', extra.medical_notes],
            ['Parent / guardian', a.guardian_name], ['Relationship', extra.guardian_relationship], ['Phone', a.guardian_phone], ['Email', a.guardian_email], ['Home address', a.address],
          ].map(([k, v]) => (
            <div key={k as string} className="flex sm:block justify-between gap-3"><dt className="text-xs text-slate-500">{k}</dt><dd className="text-slate-900 font-medium text-right sm:text-left">{(v as string) || '—'}</dd></div>
          ))}
        </dl>
      </Card>

      {/* Payments */}
      <Card title="Payments" sub={`Application fee ${naira(a.fee.amount)} · ${a.fee.paid ? 'paid' : 'not paid'}`}>
        {(a.payments ?? []).length === 0 ? <p className="text-sm text-slate-500">No payment attempts yet.</p> : (
          <ul className="divide-y divide-slate-100">
            {a.payments.map((p: any) => (
              <li key={p.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0"><span className="block font-medium text-slate-900">{p.gateway === 'paystack' ? 'Paystack' : p.gateway === 'manual' ? 'Bank transfer (receipt)' : ['cash', 'pos'].includes(p.method) ? p.method.toUpperCase() : 'School account transfer'}</span>
                  <span className="block text-xs text-slate-500 font-mono">{p.reference}{p.rejection_reason ? ` · rejected: ${p.rejection_reason}` : ''}</span></span>
                <span className="text-right shrink-0"><span className="block font-semibold">{naira(p.amount)}</span><Pill tone={p.status === 'successful' ? 'green' : p.status === 'pending' ? 'amber' : 'slate'}>{p.status === 'pending' && p.review === 'pending' ? 'to verify' : p.status}</Pill></span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ── modals ── */}
      {modal === 'admit' && (
        <AdmitModal arms={arms} groupName={a.class_applied?.name} busy={run.isPending} onClose={() => setModal(null)}
          onSubmit={(classId, note) => act(() => admit(a.id, { class_id: classId, note }), 'Admitted.')} />
      )}
      {modal === 'reject' && (
        <DecisionModal busy={run.isPending} onClose={() => setModal(null)} onSubmit={(decision, note) => act(() => decide(a.id, { decision, note }), decision === 'waitlist' ? 'Waitlisted.' : 'Marked as not admitted.')} />
      )}
      {modal === 'office' && (
        <Modal title="Payment received at the office" onClose={() => setModal(null)}>
          <form onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.currentTarget); act(() => confirmPayment(a.id, { method: String(f.get('method')), note: String(f.get('note') || '') || undefined }), 'Payment recorded.'); }} className="space-y-3">
            <Field label="How was it paid?"><select name="method" className={input}><option value="cash">Cash</option><option value="pos">POS</option><option value="bank_deposit">Bank deposit</option></select></Field>
            <Field label="Note (optional)"><input name="note" className={input} placeholder="e.g. Teller number" /></Field>
            <Btn type="submit" disabled={run.isPending} className="w-full">Record {naira(a.fee.amount)}</Btn>
          </form>
        </Modal>
      )}
      {modal === 'reject_receipt' && target && (
        <Modal title="Reject this receipt" onClose={() => setModal(null)}>
          <form onSubmit={(e) => { e.preventDefault(); act(() => rejectPayment(target.id, String(new FormData(e.currentTarget).get('reason'))), 'Receipt rejected.'); }} className="space-y-3">
            <Field label="Tell the applicant why"><textarea name="reason" required rows={3} className={input} placeholder="e.g. We did not receive this transfer. Please check the reference." /></Field>
            <Btn type="submit" kind="danger" disabled={run.isPending} className="w-full">Reject receipt</Btn>
          </form>
        </Modal>
      )}
      {modal === 'request_doc' && (
        <Modal title="Request a document" onClose={() => setModal(null)}>
          <form onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.currentTarget); act(() => requestDocument(a.id, { type: String(f.get('type')), note: String(f.get('note') || '') || undefined }), 'Document requested.'); }} className="space-y-3">
            <Field label="Document"><select name="type" className={input}>{(a.required_documents ?? []).map((d: any) => <option key={d.type} value={d.type}>{d.label}</option>)}<option value="other">Other</option></select></Field>
            <Field label="Message (optional)"><input name="note" className={input} placeholder="e.g. The photo is blurry" /></Field>
            <Btn type="submit" disabled={run.isPending} className="w-full">Send request</Btn>
          </form>
        </Modal>
      )}
      {modal === 'score' && (
        <Modal title="Exam score" onClose={() => setModal(null)}>
          <form onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.currentTarget); const absent = f.get('absent') === 'on';
            act(() => examScore(a.id, absent ? { absent: true } : { score: Number(f.get('score')), max_score: Number(f.get('max')) }), 'Score saved.'); }} className="space-y-3">
            <div className="grid grid-cols-2 gap-3"><Field label="Score"><input name="score" type="number" step="0.5" min={0} className={input} /></Field><Field label="Out of"><input name="max" type="number" min={1} defaultValue={100} className={input} /></Field></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="absent" className="accent-green-600" /> Mark absent</label>
            <Btn type="submit" disabled={run.isPending} className="w-full">Save</Btn>
          </form>
        </Modal>
      )}
    </Drawer>
  );
}

function AdmitModal({ arms, groupName, busy, onClose, onSubmit }: { arms: any[]; groupName?: string; busy: boolean; onClose: () => void; onSubmit: (classId: number, note?: string) => void }) {
  const [cls, setCls] = useState<number | ''>(arms.length === 1 ? arms[0].id : '');
  const [note, setNote] = useState('');
  return (
    <Modal title={`Admit into ${groupName ?? 'a class'}`} onClose={onClose}
      footer={<><Btn kind="ghost" onClick={onClose}>Cancel</Btn><Btn disabled={!cls || busy} onClick={() => cls && onSubmit(Number(cls), note || undefined)}>{busy ? 'Admitting…' : 'Admit as student'}</Btn></>}>
      <p className="text-sm text-slate-600 mb-4">They become a student of the arm you pick now. A reg number is generated and sent with the default password to their dashboard and by SMS.</p>
      <Field label="Class arm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {arms.map((c) => (
            <button key={c.id} type="button" onClick={() => setCls(c.id)} className={`text-left border rounded-lg px-3 py-2.5 text-sm ${cls === c.id ? 'border-green-600 bg-green-50 ring-2 ring-green-600/20' : 'border-slate-200 hover:bg-slate-50'}`}>
              <span className="font-semibold text-slate-900">{c.name}</span>{c.capacity ? <span className="block text-xs text-slate-500">Capacity {c.capacity}</span> : null}
            </button>
          ))}
        </div>
      </Field>
      <div className="mt-3"><Field label="Note (optional)"><input value={note} onChange={(e) => setNote(e.target.value)} className={input} /></Field></div>
    </Modal>
  );
}

function DecisionModal({ busy, onClose, onSubmit }: { busy: boolean; onClose: () => void; onSubmit: (d: 'waitlist' | 'reject', note?: string) => void }) {
  const [d, setD] = useState<'waitlist' | 'reject'>('waitlist');
  const [note, setNote] = useState('');
  return (
    <Modal title="Waitlist or reject" onClose={onClose} footer={<><Btn kind="ghost" onClick={onClose}>Cancel</Btn><Btn kind={d === 'reject' ? 'danger' : 'dark'} disabled={busy} onClick={() => onSubmit(d, note || undefined)}>Save decision</Btn></>}>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {(['waitlist', 'reject'] as const).map((k) => (
          <button key={k} type="button" onClick={() => setD(k)} className={`border rounded-lg px-3 py-2.5 text-sm font-semibold ${d === k ? 'border-[#0f172a] bg-slate-50' : 'border-slate-200'}`}>{k === 'waitlist' ? 'Waitlist' : 'Not admitted'}</button>
        ))}
      </div>
      <Field label="Message to the applicant (optional)"><textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={input} /></Field>
    </Modal>
  );
}
